import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPenToSquare,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { baseURL } from '../../Constants';
import { Article } from '../../context/NewsContext';
import { AuthContext } from '../../context/AuthContext';
import { analytics } from '../../services/analytics';
import '../../styles/admin/admin-article.scss';

const AdminArticlesPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  
  const navigate = useNavigate();
  const { token, isAdmin } = useContext(AuthContext);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // Check if user is authenticated and is an admin
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);

  useEffect(() => {
    // Set page title
    document.title = 'Manage Articles - Admin Dashboard';
    
    // Fetch all articles
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${baseURL}/NewsArticle`, {
          headers: getAuthHeaders()
        });
        // Sort articles by date (newest first)
        const sortedArticles = response.data.sort((a: Article, b: Article) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setArticles(sortedArticles);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError('Failed to load articles. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchArticles();
    }
  }, [refreshKey, token]);

  const handleDeleteArticle = async (id: string) => {
    if (!token) {
      setError('Authentication required to delete articles');
      navigate('/admin/login');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this article? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${baseURL}/NewsArticle/${id}`, {
        headers: getAuthHeaders()
      });
      // Track article deletion
      analytics.admin.articleDeleted(id);
      // Refresh the article list
      setRefreshKey(prevKey => prevKey + 1);
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Failed to delete article. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="admin-page admin-articles-page loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-articles-page">
      <div className="container">
        <div className="admin-header">
          <h1>Manage Articles</h1>
          <Link to="/admin/articles/new" className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} /> CREATE NEW ARTICLE
          </Link>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}
        
        <div className="admin-content">
          <div className="articles-table-container">
            <table className="articles-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.length > 0 ? (
                  articles.map(article => (
                    <tr key={article.id}>
                      <td className="article-title">
                        <Link to={`/news/${article.id}`} target="_blank">
                          {article.title}
                        </Link>
                      </td>
                      <td>{article.category}</td>
                      <td>{new Date(article.date).toLocaleDateString()}</td>
                      <td className="actions">
                        <Link 
                          to={`/admin/articles/edit/${article.id}`} 
                          className="btn btn-sm btn-edit"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Link>
                        <button 
                          onClick={() => handleDeleteArticle(article.id)} 
                          className="btn btn-sm btn-delete"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-articles">
                      No articles found. Create your first article using the "Create New Article" button.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminArticlesPage;